import { BlocksFrameAppShell } from "@blocks/helios-frames-react";
import {
  appI18nIntegration,
  type I18nResourceBundle,
} from "@blocks/helios-frames";
import { msalConfig } from "@/shared/configs/msalConfig";
import { HEADER_CONFIG, APP_NAME, ARIA_LABELS } from "@/shared/constants";
import { logger } from "@/shared/utils/logger";
import SideMenu from "../SideMenu";
import "./AppShell.scss";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "@store/store";
import { setUser, setLanguage, logout } from "@store/userSlice";
import {
  syncDocumentLanguage,
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from "../../../i18n";
import { CatDropdown } from "@cat-digital-blocks/react/bedrock/components/Dropdown";
import { CatList } from "@cat-digital-blocks/react/bedrock/components/List";
import { CatListItem } from "@cat-digital-blocks/react/bedrock/components/ListItem";
import { CatButton } from "@cat-digital-blocks/react/bedrock/components/Button";
import { CatIconSyncronize } from "@cat-digital-blocks/react/bedrock/components/Icons/Syncronize";
import { CatHeading } from "@cat-digital-blocks/react/bedrock/components/Heading";

// Language display names for the profile dropdown
const LANGUAGE_PREFERENCES = ["English", "Español", "Français", "Deutsch"];

// Custom profile menu item for dealer selection
const PROFILE_MENU_DEALER_TITLE = "Select Dealer";

/**
 * Type guard to check if a language string is a supported language
 */
const isSupportedLanguage = (lang: string): lang is SupportedLanguage => {
  return SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage);
};

interface AppShellProps {
  children: React.ReactNode;
  dealerCode?: string;
  availableDealers?: string[];
  onDealerChange?: (dealerCode: string) => void;
  isLoadingDealers?: boolean;
  /** User roles for role-based menu visibility */
  userRoles?: string[];
}

/**
 * AppShell Component
 *
 * Wraps the entire application with Blocks Frame authentication and layout.
 * Includes header, side navigation, and main content area.
 * Integrates with Helios Frames i18n system for language synchronization.
 * Dispatches user state to Redux store on authentication events.
 *
 * @param children - Main content to display
 * @param dealerCode - Currently selected dealer code
 * @param availableDealers - List of available dealer codes
 * @param onDealerChange - Callback when dealer code changes
 * @param isLoadingDealers - Whether dealers are being fetched
 */
const AppShell = ({
  children,
  dealerCode,
  availableDealers,
  onDealerChange,
  isLoadingDealers,
  userRoles,
}: AppShellProps) => {
  const dispatch = useAppDispatch();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [currentLanguage, setCurrentLanguage] =
    useState<SupportedLanguage>(DEFAULT_LANGUAGE);
  const [translations, setTranslations] = useState<I18nResourceBundle>({});
  const loadedLanguagesRef = useRef<Set<string>>(new Set());
  const { i18n, t } = useTranslation();

  // Dealer selection modal state
  const [isDealerModalOpen, setIsDealerModalOpen] = useState(false);
  const [selectedDealer, setSelectedDealer] = useState<string>(
    dealerCode || "",
  );

  // Custom preference data for profile menu - includes dealer selector
  const customPreferenceData = useMemo(
    () => [
      {
        profileCustomMenuTitle: PROFILE_MENU_DEALER_TITLE,
        profileCustomeMenuDesc: dealerCode
          ? `${t("navigation.currentDealer")}: ${dealerCode}`
          : t("navigation.selectDealer"),
      },
    ],
    [dealerCode, t],
  );

  // Sync selected dealer with prop
  useEffect(() => {
    if (dealerCode) {
      setSelectedDealer(dealerCode);
    }
  }, [dealerCode]);

  // Sync document language attribute and direction
  useEffect(() => {
    document.documentElement.lang = i18n.language;
    document.documentElement.dir = i18n.dir();
  }, [i18n, i18n.language]);

  /**
   * Load translation for a specific language
   * Caches loaded translations to avoid re-fetching
   */
  const loadTranslationForLanguage = useCallback(
    async (language: string): Promise<Record<string, unknown>> => {
      if (loadedLanguagesRef.current.has(language)) {
        return translations[language] || {};
      }

      try {
        const response = await fetch(`/i18n/${language}.json`);
        if (!response.ok) {
          throw new Error(
            `Failed to fetch /i18n/${language}.json: ${response.status}`,
          );
        }
        const translationData = await response.json();
        setTranslations((prev) => ({ ...prev, [language]: translationData }));
        loadedLanguagesRef.current.add(language);
        return translationData;
      } catch (error) {
        logger.error(
          "Failed to load translation for language:",
          language,
          error,
        );
        return {};
      }
    },
    [translations],
  );

  /**
   * Change language - updates i18n, syncs with Helios Frames, and updates document
   */
  const changeLanguage = useCallback(
    async (lng: string) => {
      // Validate language is supported using type guard
      if (!isSupportedLanguage(lng)) {
        logger.warn("Unsupported language:", lng);
        return;
      }

      try {
        await loadTranslationForLanguage(lng);
        await i18n.changeLanguage(lng);
        setCurrentLanguage(lng);
        syncDocumentLanguage(lng);

        // Sync with Helios Frames library - pass current translations
        await appI18nIntegration.handleLanguageChange(lng, translations);
        logger.info("Language changed to:", lng);
      } catch (error) {
        logger.error("Error changing language:", error);
      }
    },
    [i18n, loadTranslationForLanguage, translations],
  );

  // Load initial translation on mount
  useEffect(() => {
    const loadInitialTranslation = async () => {
      const currentLang = i18n.language || DEFAULT_LANGUAGE;
      await loadTranslationForLanguage(currentLang);
      if (isSupportedLanguage(currentLang)) {
        setCurrentLanguage(currentLang);
      }
    };
    loadInitialTranslation();
  }, [i18n.language, loadTranslationForLanguage]);

  /**
   * Handle language change from Helios Frames profile dropdown
   */
  const handleLanguageChange = async (event: CustomEvent) => {
    const { language } = event.detail || {};
    if (!language) {
      logger.warn("Language missing in event:", event);
      return;
    }
    await changeLanguage(language);
  };

  /**
   * Handle user profile changes including language preference
   */
  const handleUserProfileChange = (event: CustomEvent) => {
    const language =
      event?.detail?.userProfileData?.preference?.language ||
      event?.detail?.value?.language ||
      event?.detail?.language;

    if (language && i18n.language !== language) {
      changeLanguage(language);
    }
  };

  /**
   * Handle user profile selection (required for profile menu interactions)
   * Opens dealer selection modal when "Select Dealer" is clicked
   */
  const handleUserPreference = useCallback((event: CustomEvent) => {
    const selectedValue = event.detail?.value;
    logger.debug("User preference selected:", selectedValue);

    if (selectedValue === PROFILE_MENU_DEALER_TITLE) {
      setIsDealerModalOpen(true);
    }
  }, []);

  /**
   * Handle dealer selection from modal dropdown
   */
  const handleDealerSelect = useCallback((e: CustomEvent) => {
    const dealer = e.detail?.value;
    if (dealer) {
      setSelectedDealer(dealer);
    }
  }, []);

  /**
   * Confirm dealer selection and close modal
   */
  const handleDealerConfirm = useCallback(() => {
    if (selectedDealer && onDealerChange) {
      onDealerChange(selectedDealer);
    }
    setIsDealerModalOpen(false);
  }, [selectedDealer, onDealerChange]);

  /**
   * Cancel dealer selection and close modal
   */
  const handleDealerModalClose = useCallback(() => {
    setSelectedDealer(dealerCode || "");
    setIsDealerModalOpen(false);
  }, [dealerCode]);

  const handleSignIn = (event: CustomEvent) => {
    logger.info("User signed in:", event.detail);
    setIsAuthenticated(true);

    const userInfo =
      event.detail?.value || event.detail?.account || event.detail || {};

    // Log user info structure for debugging
    logger.debug("User info structure:", JSON.stringify(userInfo, null, 2));

    const userName = userInfo.username || userInfo.email || "";
    // Combine firstName + lastName for full name
    const fullName =
      userInfo.firstName && userInfo.lastName
        ? `${userInfo.firstName} ${userInfo.lastName}`
        : "";
    const email = userInfo.email || "";

    logger.debug(
      "Parsed user - userName:",
      userName,
      "fullName:",
      fullName,
      "email:",
      email,
    );

    // Dispatch user info to Redux store
    if (userName) {
      dispatch(setUser({ userName, fullName, email }));
    }

    // Set initial language from user profile if available
    const userLanguage =
      userInfo.language || userInfo.locale || DEFAULT_LANGUAGE;
    if (userLanguage !== i18n.language && isSupportedLanguage(userLanguage)) {
      changeLanguage(userLanguage);
      dispatch(setLanguage(userLanguage));
    }
  };

  const handleSignOut = () => {
    logger.info("User signed out");
    setIsAuthenticated(false);
    dispatch(logout());
  };

  const handleMobileMenuClick = () => {
    setIsSidebarCollapsed((prev) => !prev);
  };

  return (
    <div className="app-container">
      <BlocksFrameAppShell //question
        msal={msalConfig}
        OnBFUserProfileSignIn={handleSignIn}
        OnBFUserProfileSignOut={handleSignOut}
        OnBFUserProfileChange={handleUserProfileChange}
        OnBFLanguageChange={handleLanguageChange}
        OnBFUserProfileSelect={handleUserPreference}
        onBFMobileMenuClick={handleMobileMenuClick}
        isMobileMenuButton={true}
        languagePreferencesData={LANGUAGE_PREFERENCES}
        customPreferenceData={customPreferenceData}
        currentLanguage={currentLanguage}
        translations={translations}
        enableAutoLogin={true}
        logoText={APP_NAME}
        logoLink="/"
        fullWidth={true}
        isFooter={true}
        headerButtonVisibility={HEADER_CONFIG}
      >
        <div slot="bf-slot-app" className="app-content-wrapper">
          {isAuthenticated ? (
            <div className="authenticated-app-container">
              <div
                className={`sidebar-wrapper ${
                  isSidebarCollapsed ? "sidebar-collapsed" : ""
                }`}
              >
                <SideMenu userRoles={userRoles} />
              </div>
              <div className="main-content">{children}</div>
            </div>
          ) : null}
        </div>

        {/* Dealer Selection Modal */}
        {isDealerModalOpen && (
          <div className="dealer-modal-overlay">
            <div className="dealer-modal">
              <div className="dealer-modal-header">
                <CatHeading variant="title-sm">
                  {t("navigation.selectDealer")}
                </CatHeading>
              </div>
              <div className="dealer-modal-content">
                {isLoadingDealers ? (
                  <div className="dealer-loading">
                    <CatIconSyncronize />
                    <span>{t("auth.loadingDealers")}</span>
                  </div>
                ) : !availableDealers || availableDealers.length === 0 ? (
                  <div className="dealer-empty-state">
                    <span>
                      {t(
                        "auth.noDealersAvailable",
                        "No dealers available for selection",
                      )}
                    </span>
                  </div>
                ) : (
                  <CatDropdown
                    value={selectedDealer}
                    onBlSelect={handleDealerSelect}
                    placeholder={t("navigation.selectDealer")}
                    aria-label={ARIA_LABELS.SELECT_DEALER}
                    label={t("navigation.dealerCode")}
                    className="dealer-modal-dropdown"
                  >
                    <CatList>
                      {availableDealers?.map((dealer) => (
                        <CatListItem key={dealer} value={dealer}>
                          {dealer}
                        </CatListItem>
                      ))}
                    </CatList>
                  </CatDropdown>
                )}
              </div>
              <div className="dealer-modal-footer">
                <CatButton variant="contrast" onClick={handleDealerModalClose}>
                  {t("common.cancel")}
                </CatButton>
                <CatButton
                  variant="primary"
                  onClick={handleDealerConfirm}
                  disabled={
                    !selectedDealer ||
                    !availableDealers ||
                    availableDealers.length === 0
                  }
                >
                  {t("common.confirm")}
                </CatButton>
              </div>
            </div>
          </div>
        )}
      </BlocksFrameAppShell>
    </div>
  );
};

AppShell.displayName = "AppShell";

export default AppShell;
